import {
  ACTIVE_PIPELINE_FULFILLMENT_STATUSES,
  MarketplaceFulfillmentStatusSchema,
  rollupOrderFulfillmentStatus,
  type MarketplaceFulfillmentStatus,
} from "@nexus/contracts";
import { getLegacyLegalTransitions } from "@nexus/order-status";

import type {
  MarketplaceOrder,
  OrderScriptRow,
} from "../types/marketplace.types";
import { effectiveScriptFulfillmentStatus } from "./effective-script-fulfillment";
import { usesVideoStatusChips } from "./line-status-display";
import { scriptMatchesStatusFilter } from "./status-filter-match";
import { videoFulfillmentStatusChips } from "./video-display-status";
import {
  requiresVideoSelectionForRevision,
  resolveRevisionDeliverableId,
  scriptHasVideoAwaitingRevision,
} from "./revision-deliverable";
import {
  describeDeadline,
  orderDeadlineYmd,
  type DeadlineTone,
} from "./order-deadline";
import {
  orderLineMatchesTypeFilter,
  resolveOrderType,
  type OrderLineTypeFilterOption,
  type OrderType,
} from "./order-line-kind";

export const ORDERS_GROUP_BY_OPTIONS = [
  "none",
  "vendor",
  "status",
  "deadline",
] as const;

/** Hub Production groups one way at a time. Vendor grouping stays on the marketplace list. */
export const HUB_ORDERS_GROUP_BY_OPTIONS = ["none", "status", "deadline"] as const;
export type OrdersGroupBy = (typeof ORDERS_GROUP_BY_OPTIONS)[number];

export const ORDERS_SORT_BY_OPTIONS = [
  "deadline",
  "createdAt",
  "vendor",
  "scripts",
  "status",
] as const;
export type OrdersSortBy = (typeof ORDERS_SORT_BY_OPTIONS)[number];
export type OrdersSortDirection = "asc" | "desc";
export type OrdersDensity = "compact" | "comfortable";

export const DEADLINE_GROUP_KEYS = [
  "overdue",
  "today",
  "thisWeek",
  "later",
  "unscheduled",
] as const;
export type DeadlineGroupKey = (typeof DEADLINE_GROUP_KEYS)[number];

export interface OrdersListQuery {
  search: string;
  /** Empty means every status. */
  statuses: MarketplaceFulfillmentStatus[];
  /** Empty means every vendor. */
  vendorIds: string[];
  /** Overdue, waiting on the brand's approval, or a failed vendor handoff. */
  attentionOnly: boolean;
  overdueOnly: boolean;
  dueFrom: string;
  dueTo: string;
  lineType: OrderLineTypeFilterOption;
  groupBy: OrdersGroupBy;
  sortBy: OrdersSortBy;
  sortDirection: OrdersSortDirection;
  density: OrdersDensity;
}

export const DEFAULT_ORDERS_LIST_QUERY: OrdersListQuery = {
  search: "",
  statuses: [],
  vendorIds: [],
  attentionOnly: false,
  overdueOnly: false,
  dueFrom: "",
  dueTo: "",
  lineType: "all",
  groupBy: "none",
  sortBy: "deadline",
  sortDirection: "asc",
  density: "compact",
};

/** `orderType` comes from the untrimmed lines, so filtering a mixed order still reads Mixed. */
export type OrdersListOrder = MarketplaceOrder & {
  orderType: OrderType;
  /** Lines every filter but Status keeps; equals `scripts` without a Status filter. */
  scriptsWithoutStatusFilter: OrderScriptRow[];
  /** Lines the Status filter matched; null without a Status filter. */
  statusMatchedScriptIds: ReadonlySet<string> | null;
};

/** Lines the Status filter hid in this order; 0 once the order is revealed. */
export function statusHiddenLineCount(order: OrdersListOrder): number {
  return order.scriptsWithoutStatusFilter.length - order.scripts.length;
}

/** The order with the lines the Status filter hid shown again. */
export function revealStatusHiddenLines(order: OrdersListOrder): OrdersListOrder {
  return { ...order, scripts: order.scriptsWithoutStatusFilter };
}

export interface OrdersGroup<T extends MarketplaceOrder = OrdersListOrder> {
  key: string;
  orders: T[];
}

export interface OrderAttention {
  overdue: boolean;
  awaitingApproval: boolean;
  handoffFailed: boolean;
}

export interface StatusMix {
  /** What the order reads as — the least-advanced script status (mirrors the server rollup). */
  primary: MarketplaceFulfillmentStatus;
  counts: Partial<Record<MarketplaceFulfillmentStatus, number>>;
  isMixed: boolean;
}

export interface StatusRollup extends StatusMix {
  total: number;
  uniformStatus: MarketplaceFulfillmentStatus | null;
  /** Most useful non-uniform state to name after "Mixed". */
  mentionStatus: MarketplaceFulfillmentStatus;
}

export interface DeadlineRollup {
  earliestYmd: string | null;
  overdueCount: number;
  scheduledCount: number;
  isUniform: boolean;
  source: "scripts" | "order" | "none";
}

/** Pipeline order used for status sort and grouping — mirrors the contracts rank. */
const STATUS_ORDER: readonly MarketplaceFulfillmentStatus[] =
  MarketplaceFulfillmentStatusSchema.options;

const MIXED_STATUS_MENTION_ORDER: readonly MarketplaceFulfillmentStatus[] = [
  "SENT_FOR_REVISION",
  "SUBMITTED_FOR_FINAL_APPROVAL",
  "SUBMITTED_FOR_FEEDBACK",
  "SENT_TO_PRODUCTION",
  "SCRIPT",
  "ORDER_DELIVERED",
  "COMPLETED_PRE_ADGE",
  "CANCELLED",
];

const DEADLINE_GROUP_ORDER: Record<DeadlineGroupKey, number> = {
  overdue: 0,
  today: 1,
  thisWeek: 2,
  later: 3,
  unscheduled: 4,
};

export function describeStatusMix(order: MarketplaceOrder): StatusMix {
  const statuses = order.scripts.map((script) =>
    effectiveScriptFulfillmentStatus(script),
  );
  const counts: Partial<Record<MarketplaceFulfillmentStatus, number>> = {};
  for (const status of statuses) {
    counts[status] = (counts[status] ?? 0) + 1;
  }
  const primary =
    statuses.length > 0
      ? rollupOrderFulfillmentStatus(statuses)
      : order.fulfillmentStatus;
  return { primary, counts, isMixed: Object.keys(counts).length > 1 };
}

export function statusRollupFromCounts(
  counts: Partial<Record<MarketplaceFulfillmentStatus, number>>,
  fallbackPrimary: MarketplaceFulfillmentStatus,
): StatusRollup {
  const total = Object.values(counts).reduce(
    (sum, count) => sum + (count ?? 0),
    0,
  );
  const activeStatuses = (
    Object.entries(counts) as Array<[MarketplaceFulfillmentStatus, number]>
  ).filter(([, count]) => count > 0);
  const isMixed = activeStatuses.length > 1;
  const statuses = activeStatuses.flatMap(([status, count]) =>
    Array.from({ length: count }, () => status),
  );
  const primary =
    statuses.length > 0
      ? rollupOrderFulfillmentStatus(statuses)
      : fallbackPrimary;
  const uniformStatus = isMixed ? null : (activeStatuses[0]?.[0] ?? null);
  const mentionStatus =
    MIXED_STATUS_MENTION_ORDER.find((status) => (counts[status] ?? 0) > 0) ??
    primary;
  return {
    primary,
    counts,
    isMixed,
    total,
    uniformStatus,
    mentionStatus,
  };
}

function orderVendorWorkingWeekdays(order: MarketplaceOrder) {
  return order.vendor.workingWeekdays;
}

/**
 * Status counts behind an order's rollup chip. A line whose videos disagree
 * contributes its video statuses, as its own Mixed chip does, so a mixed line
 * makes the order read Mixed too.
 */
function rollupStatusCounts(
  order: MarketplaceOrder,
): Partial<Record<MarketplaceFulfillmentStatus, number>> {
  const counts: Partial<Record<MarketplaceFulfillmentStatus, number>> = {};
  for (const script of order.scripts) {
    const videoChips = usesVideoStatusChips(script)
      ? videoFulfillmentStatusChips(script)
      : [];
    // A line whose videos agree still counts once, so a uniform order reads "1/1".
    const chips =
      videoChips.length > 1
        ? videoChips
        : [{ status: effectiveScriptFulfillmentStatus(script), count: 1 }];
    for (const chip of chips) {
      counts[chip.status] = (counts[chip.status] ?? 0) + chip.count;
    }
  }
  return counts;
}

/** The order's chip; `primary` stays the line rollup that grouping and sorting use. */
export function describeStatusRollup(order: MarketplaceOrder): StatusRollup {
  const mix = describeStatusMix(order);
  const rollup = statusRollupFromCounts(rollupStatusCounts(order), mix.primary);
  return { ...rollup, primary: mix.primary };
}

export function describeDeadlineRollup(
  order: MarketplaceOrder,
  todayYmd: string,
): DeadlineRollup {
  const deadlineScripts = order.scripts.filter(
    (script) => script.fulfillmentStatus !== "CANCELLED",
  );
  const scriptDates = deadlineScripts
    .map((script) => script.expectedDeliveryDate)
    .filter((date): date is string => Boolean(date));
  const dates =
    deadlineScripts.length === 0
      ? []
      : scriptDates.length > 0
        ? scriptDates
        : order.etaDate
          ? [order.etaDate]
          : [];
  const workingWeekdays = orderVendorWorkingWeekdays(order);
  const overdueCount = scriptDates.filter(
    (date) =>
      describeDeadline(date, todayYmd, workingWeekdays)?.tone === "overdue",
  ).length;
  return {
    earliestYmd: [...dates].sort()[0] ?? null,
    overdueCount,
    scheduledCount: scriptDates.length,
    isUniform:
      deadlineScripts.length > 0 &&
      scriptDates.length === deadlineScripts.length &&
      new Set(scriptDates).size === 1,
    source:
      scriptDates.length > 0
        ? "scripts"
        : deadlineScripts.length > 0 && order.etaDate
          ? "order"
          : "none",
  };
}

/** The date represented in the parent Due column: earliest script, then order ETA fallback. */
export function orderListDeadlineYmd(order: MarketplaceOrder): string | null {
  const scripts = order.scripts.filter(
    (script) => script.fulfillmentStatus !== "CANCELLED",
  );
  if (order.scripts.length > 0 && scripts.length === 0) return null;
  return orderDeadlineYmd({ etaDate: order.etaDate, scripts });
}

/** Scripts whose due date the tracker filters on and an Extend can move. */
const OPEN_DEADLINE_STATUSES = new Set<MarketplaceFulfillmentStatus>([
  "SCRIPT",
  ...ACTIVE_PIPELINE_FULFILLMENT_STATUSES,
]);

export function isOpenDeadlineScript(
  script: Pick<OrderScriptRow, "fulfillmentStatus">,
): boolean {
  return OPEN_DEADLINE_STATUSES.has(script.fulfillmentStatus);
}

/**
 * The earliest date an order-scope Extend moves: open scripts only, or the
 * ETA of an order with no scripts. Delivered lines are history, not deadlines.
 */
export function openOrderDeadlineYmd(order: MarketplaceOrder): string | null {
  if (order.scripts.length === 0) return order.etaDate;
  return (
    order.scripts
      .filter(isOpenDeadlineScript)
      .map((script) => script.expectedDeliveryDate)
      .filter((date): date is string => Boolean(date))
      .sort()[0] ?? null
  );
}

export function describeOrderAttention(
  order: MarketplaceOrder,
  todayYmd: string,
): OrderAttention {
  const actionableScripts = order.scripts.filter(
    (script) => script.fulfillmentStatus !== "CANCELLED",
  );
  if (order.scripts.length > 0 && actionableScripts.length === 0) {
    return { overdue: false, awaitingApproval: false, handoffFailed: false };
  }
  const workingWeekdays = orderVendorWorkingWeekdays(order);
  const deadline = describeDeadline(
    orderListDeadlineYmd(order),
    todayYmd,
    workingWeekdays,
  );
  const scriptDeadlines = actionableScripts.map((script) =>
    describeDeadline(
      script.expectedDeliveryDate,
      todayYmd,
      workingWeekdays,
    ),
  );
  const overdue =
    deadline?.tone === "overdue" ||
    scriptDeadlines.some((info) => info?.tone === "overdue");
  const awaitingApproval = actionableScripts.some(
    (script) => script.fulfillmentStatus === "SUBMITTED_FOR_FINAL_APPROVAL",
  );
  const handoffFailed = order.status === "FAILED";
  return { overdue, awaitingApproval, handoffFailed };
}

export function needsAttention(attention: OrderAttention): boolean {
  return (
    attention.overdue || attention.awaitingApproval || attention.handoffFailed
  );
}

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

function matchesScriptSearch(script: OrderScriptRow, needle: string): boolean {
  const snapshot = script.scriptContentSnapshot ?? {};
  const haystack = [
    script.script?.concept,
    script.script?.hook,
    snapshot["assetName"],
    snapshot["title"],
    snapshot["hook"],
  ]
    .filter((value): value is string => typeof value === "string")
    .map(normalize);
  return haystack.some((text) => text.includes(needle));
}

/** Free-text match on vendor, order id, and script concept/hook/asset name. */
export function matchesSearch(
  order: MarketplaceOrder,
  search: string,
): boolean {
  const needle = normalize(search);
  return (
    !needle ||
    normalize(order.vendor.name).includes(needle) ||
    normalize(order.id).includes(needle) ||
    order.scripts.some((script) => matchesScriptSearch(script, needle))
  );
}

function filterOrderScripts(
  order: MarketplaceOrder,
  query: OrdersListQuery,
  todayYmd: string,
  statusSet: ReadonlySet<MarketplaceFulfillmentStatus>,
): OrderScriptRow[] {
  const needle = normalize(query.search);
  const orderIdentityMatches =
    !needle ||
    normalize(order.vendor.name).includes(needle) ||
    normalize(order.id).includes(needle);
  const hasScriptDeadlines = order.scripts.some((script) =>
    script.fulfillmentStatus !== "CANCELLED" &&
    Boolean(script.expectedDeliveryDate),
  );
  const fallbackDeadline = hasScriptDeadlines ? null : order.etaDate;
  const isHandoffFailure = order.status === "FAILED";

  return order.scripts.filter((script) => {
    if (!orderLineMatchesTypeFilter(script, query.lineType)) return false;
    if (!scriptMatchesStatusFilter(script, statusSet)) return false;
    if (!orderIdentityMatches && !matchesScriptSearch(script, needle))
      return false;

    const dueYmd =
      script.fulfillmentStatus === "CANCELLED"
        ? null
        : (script.expectedDeliveryDate ?? fallbackDeadline);
    const deadline = describeDeadline(
      dueYmd,
      todayYmd,
      orderVendorWorkingWeekdays(order),
    );
    if (query.overdueOnly && deadline?.tone !== "overdue") return false;
    if (query.dueFrom && (!dueYmd || dueYmd < query.dueFrom)) return false;
    if (query.dueTo && (!dueYmd || dueYmd > query.dueTo)) return false;

    if (
      query.attentionOnly &&
      !isHandoffFailure &&
      deadline?.tone !== "overdue" &&
      script.fulfillmentStatus !== "SUBMITTED_FOR_FINAL_APPROVAL"
    ) {
      return false;
    }
    return true;
  });
}

/**
 * Orders a Status filter opens at render. Computed, never persisted, so
 * clearing the filter returns to the user's own expand state.
 */
export function statusFilterExpandedOrderIds(
  orders: readonly MarketplaceOrder[],
  statuses: readonly MarketplaceFulfillmentStatus[],
): ReadonlySet<string> {
  if (statuses.length === 0) return new Set();
  return new Set(orders.map((order) => order.id));
}

export function filterOrders(
  orders: readonly MarketplaceOrder[],
  query: OrdersListQuery,
  todayYmd: string,
): OrdersListOrder[] {
  const vendorSet = new Set(query.vendorIds);
  return orders.flatMap((order) => {
    if (vendorSet.size > 0 && !vendorSet.has(order.vendorId)) return [];
    const statusSet = new Set(query.statuses);
    const scripts = filterOrderScripts(order, query, todayYmd, statusSet);
    if (scripts.length === 0) return [];
    // Lines the other filters already hid are not the Status filter's to report.
    const isStatusFiltered = statusSet.size > 0;
    return [
      {
        ...order,
        scripts,
        orderType: resolveOrderType(order.scripts),
        scriptsWithoutStatusFilter: isStatusFiltered
          ? filterOrderScripts(order, query, todayYmd, new Set())
          : scripts,
        statusMatchedScriptIds: isStatusFiltered
          ? new Set(scripts.map((script) => script.id))
          : null,
      },
    ];
  });
}

function compareCreatedAt(
  left: MarketplaceOrder,
  right: MarketplaceOrder,
): number {
  return (
    new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime()
  );
}

function compareDeadlineAsc(
  left: MarketplaceOrder,
  right: MarketplaceOrder,
): number {
  const leftYmd = orderListDeadlineYmd(left);
  const rightYmd = orderListDeadlineYmd(right);
  if (leftYmd === rightYmd) return 0;
  // Unscheduled orders sink to the bottom — nothing to act on yet.
  if (leftYmd === null) return 1;
  if (rightYmd === null) return -1;
  return leftYmd < rightYmd ? -1 : 1;
}

export function sortOrders<T extends MarketplaceOrder>(
  orders: readonly T[],
  sortBy: OrdersSortBy,
  direction: OrdersSortDirection = "asc",
): T[] {
  const sorted = [...orders];
  let compare: (left: MarketplaceOrder, right: MarketplaceOrder) => number;
  switch (sortBy) {
    case "deadline":
      compare = (a, b) => compareDeadlineAsc(a, b) || compareCreatedAt(a, b);
      break;
    case "createdAt":
      compare = compareCreatedAt;
      break;
    case "vendor":
      compare = (a, b) =>
        a.vendor.name.localeCompare(b.vendor.name) || compareDeadlineAsc(a, b);
      break;
    case "scripts":
      compare = (a, b) =>
        a.scripts.length - b.scripts.length || compareDeadlineAsc(a, b);
      break;
    case "status":
      compare = (a, b) =>
        STATUS_ORDER.indexOf(describeStatusMix(a).primary) -
          STATUS_ORDER.indexOf(describeStatusMix(b).primary) ||
        compareDeadlineAsc(a, b);
      break;
  }
  return sorted.sort((left, right) => {
    if (sortBy === "deadline") {
      const leftYmd = orderListDeadlineYmd(left);
      const rightYmd = orderListDeadlineYmd(right);
      if (leftYmd === null && rightYmd !== null) return 1;
      if (rightYmd === null && leftYmd !== null) return -1;
    }
    return compare(left, right) * (direction === "asc" ? 1 : -1);
  });
}

export function deadlineGroupKey(tone: DeadlineTone | null): DeadlineGroupKey {
  switch (tone) {
    case "overdue":
      return "overdue";
    case "today":
      return "today";
    case "soon":
      return "thisWeek";
    case "scheduled":
      return "later";
    case null:
      return "unscheduled";
  }
}

/**
 * Bucket already-sorted orders. Group order follows the first occurrence for
 * vendor, the pipeline rank for status, and urgency for deadline.
 */
export function groupOrders<T extends MarketplaceOrder>(
  orders: readonly T[],
  groupBy: OrdersGroupBy,
  todayYmd: string,
): OrdersGroup<T>[] {
  if (groupBy === "none") return [{ key: "all", orders: [...orders] }];

  const buckets = new Map<string, T[]>();
  for (const order of orders) {
    const key =
      groupBy === "vendor"
        ? order.vendorId
        : groupBy === "status"
          ? describeStatusMix(order).primary
          : deadlineGroupKey(
              describeDeadline(
                orderListDeadlineYmd(order),
                todayYmd,
                orderVendorWorkingWeekdays(order),
              )?.tone ?? null,
            );
    const bucket = buckets.get(key);
    if (bucket) bucket.push(order);
    else buckets.set(key, [order]);
  }

  const groups = [...buckets.entries()].map(([key, bucketOrders]) => ({
    key,
    orders: bucketOrders,
  }));
  if (groupBy === "status") {
    groups.sort(
      (a, b) =>
        STATUS_ORDER.indexOf(a.key as MarketplaceFulfillmentStatus) -
        STATUS_ORDER.indexOf(b.key as MarketplaceFulfillmentStatus),
    );
  } else if (groupBy === "deadline") {
    groups.sort(
      (a, b) =>
        DEADLINE_GROUP_ORDER[a.key as DeadlineGroupKey] -
        DEADLINE_GROUP_ORDER[b.key as DeadlineGroupKey],
    );
  }
  return groups;
}

export interface VendorOption {
  id: string;
  name: string;
}

export function vendorOptions(
  orders: readonly MarketplaceOrder[],
): VendorOption[] {
  const byId = new Map<string, string>();
  for (const order of orders) {
    if (!byId.has(order.vendorId)) byId.set(order.vendorId, order.vendor.name);
  }
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function countActiveFilters(query: OrdersListQuery): number {
  return (
    (query.statuses.length > 0 ? 1 : 0) +
    (query.vendorIds.length > 0 ? 1 : 0) +
    (query.attentionOnly ? 1 : 0) +
    (query.overdueOnly ? 1 : 0) +
    (query.dueFrom || query.dueTo ? 1 : 0) +
    (query.lineType !== "all" ? 1 : 0) +
    (query.search.trim() ? 1 : 0)
  );
}

export function clearOrdersListFilters(query: OrdersListQuery): OrdersListQuery {
  return {
    ...query,
    search: "",
    statuses: [],
    vendorIds: [],
    attentionOnly: false,
    overdueOnly: false,
    dueFrom: "",
    dueTo: "",
    lineType: "all",
  };
}

/**
 * A linked order the list hides clears the filters. When it never loaded, only a
 * server-side Type filter can be the cause; anything else is pagination.
 */
export function isRevealOrderHidden({
  query,
  hasRevealTarget,
  revealOrderId,
  visibleOrders,
}: {
  query: OrdersListQuery;
  hasRevealTarget: boolean;
  revealOrderId: string | null;
  visibleOrders: readonly MarketplaceOrder[];
}): boolean {
  if (!hasRevealTarget || countActiveFilters(query) === 0) return false;
  if (!revealOrderId) return query.lineType !== "all";
  return !visibleOrders.some((order) => order.id === revealOrderId);
}

// ─── Selection ────────────────────────────────────────────────────────────────

/** Scripts are the unit of selection — order script ids are globally unique. */
export type ScriptSelection = ReadonlySet<string>;

export type OrderSelectionState = "none" | "some" | "all";

export function orderSelectionState(
  order: MarketplaceOrder,
  selection: ScriptSelection,
): OrderSelectionState {
  if (order.scripts.length === 0) return "none";
  const selectedCount = order.scripts.filter((script) =>
    selection.has(script.id),
  ).length;
  if (selectedCount === 0) return "none";
  return selectedCount === order.scripts.length ? "all" : "some";
}

export function toggleOrderSelection(
  order: MarketplaceOrder,
  selection: ScriptSelection,
): Set<string> {
  const next = new Set(selection);
  const state = orderSelectionState(order, selection);
  for (const script of order.scripts) {
    if (state === "all") next.delete(script.id);
    else next.add(script.id);
  }
  return next;
}

export function toggleScriptSelection(
  scriptId: string,
  selection: ScriptSelection,
): Set<string> {
  const next = new Set(selection);
  if (next.has(scriptId)) next.delete(scriptId);
  else next.add(scriptId);
  return next;
}

export interface BulkStatusPlan {
  /** Every selected script remains an explicit operation so side effects stay consistent. */
  scripts: Array<{ order: MarketplaceOrder; script: OrderScriptRow }>;
}

/** Resolve selected ids to script operations without changing endpoint semantics. */
export function planBulkStatusChange(
  orders: readonly MarketplaceOrder[],
  selection: ScriptSelection,
): BulkStatusPlan {
  const plan: BulkStatusPlan = { scripts: [] };
  for (const order of orders) {
    for (const script of order.scripts) {
      if (selection.has(script.id)) plan.scripts.push({ order, script });
    }
  }
  return plan;
}

function bulkSharedFulfillmentOptions(
  plan: BulkStatusPlan,
  legalByScript: ReadonlySet<MarketplaceFulfillmentStatus>[],
): MarketplaceFulfillmentStatus[] {
  const shared = MarketplaceFulfillmentStatusSchema.options.filter((status) =>
    legalByScript.every((options) => options.has(status)),
  );
  if (
    plan.scripts.some(({ script }) => requiresVideoSelectionForRevision(script))
  ) {
    return shared.filter((status) => status !== "SENT_FOR_REVISION");
  }
  return shared;
}

export function bulkFulfillmentOptions(
  plan: BulkStatusPlan,
): MarketplaceFulfillmentStatus[] {
  if (plan.scripts.length === 0) return [];
  const legalByScript = plan.scripts.map(({ order, script }) =>
    new Set(
      script.fulfillmentStatus === "CANCELLED"
        ? (["CANCELLED"] as const)
        : order.source === "IMPORTED"
          ? IMPORTED_FULFILLMENT_OPTIONS
          : getLegacyLegalTransitions(script.fulfillmentStatus, "ops"),
    ),
  );
  return bulkSharedFulfillmentOptions(plan, legalByScript);
}

export function bulkFulfillmentOptionsForOpsAdmin(
  plan: BulkStatusPlan,
): MarketplaceFulfillmentStatus[] {
  if (plan.scripts.length === 0) return [];
  const legalByScript = plan.scripts.map(({ order, script }) =>
    new Set(
      script.fulfillmentStatus === "CANCELLED"
        ? (["CANCELLED"] as const)
        : order.source === "IMPORTED"
          ? IMPORTED_FULFILLMENT_OPTIONS
          : NATIVE_FULFILLMENT_OPTIONS,
    ),
  );
  return bulkSharedFulfillmentOptions(plan, legalByScript);
}

/** Statuses a brand-side editor may set. `SCRIPT` and `COMPLETED_PRE_ADGE` are import-only. */
export const NATIVE_FULFILLMENT_OPTIONS: readonly MarketplaceFulfillmentStatus[] =
  [
    "SENT_TO_PRODUCTION",
    "SUBMITTED_FOR_FEEDBACK",
    "SENT_FOR_REVISION",
    "SUBMITTED_FOR_FINAL_APPROVAL",
    "ORDER_DELIVERED",
    "CANCELLED",
  ];

export const IMPORTED_FULFILLMENT_OPTIONS: readonly MarketplaceFulfillmentStatus[] =
  MarketplaceFulfillmentStatusSchema.options;

export function fulfillmentOptionsFor(
  order: Pick<MarketplaceOrder, "source">,
  current?: MarketplaceFulfillmentStatus,
): MarketplaceFulfillmentStatus[] {
  if (current === "CANCELLED") return ["CANCELLED"];
  if (order.source === "IMPORTED") {
    const base = IMPORTED_FULFILLMENT_OPTIONS;
    if (current && !base.includes(current)) return [current, ...base];
    return [...base];
  }
  if (!current) return [];
  return [current, ...getLegacyLegalTransitions(current, "ops")];
}

export function fulfillmentOptionsForOpsAdmin(
  order: Pick<MarketplaceOrder, "source">,
  current?: MarketplaceFulfillmentStatus,
): MarketplaceFulfillmentStatus[] {
  if (current === "CANCELLED") return ["CANCELLED"];
  const base =
    order.source === "IMPORTED"
      ? IMPORTED_FULFILLMENT_OPTIONS
      : NATIVE_FULFILLMENT_OPTIONS;
  if (current && !base.includes(current)) return [current, ...base];
  return [...base];
}

export function fulfillmentOptionsForOpsAdminScript(
  order: Pick<MarketplaceOrder, "source">,
  script: OrderScriptRow,
  selectedVideoKey?: string | null,
): MarketplaceFulfillmentStatus[] {
  const options = fulfillmentOptionsForOpsAdmin(
    order,
    script.fulfillmentStatus,
  );

  if (scriptHasVideoAwaitingRevision(script)) return options;
  if (!requiresVideoSelectionForRevision(script)) return options;
  if (resolveRevisionDeliverableId(script, selectedVideoKey)) return options;
  return options.filter((status) => status !== "SENT_FOR_REVISION");
}
